import * as React from 'react';
import { connect } from 'react-redux';
import { throttle } from 'throttle-debounce';
import { listLoop } from 'lib/helpers';
import { updateStyle } from 'lib/domHelpers';
import { getTransform, angles, getPolygonStyleFactory, getPropName } from 'pages/document/DocumentComponent/helpers';
import { DimensionsFactory } from 'pages/document/page/components/ApprovalStampManagement/helpers';

import { is } from 'immutable';

import { TextractTableContainer } from 'components/TextractCanvas/container';
import { Page as PDFPage } from 'react-pdf';
import Box from '@mui/material/Box';
import Polygons from './components/Polygons/index';
import Grid from './components/Grid';
import ApprovalStampManagement from 'pages/document/page/components/ApprovalStampManagement';
import PaidBadge from 'pages/document/page/components/PaidBadge';
import DocumentPageControls from '../components/DocumentPageControls';
import { PageArea, PageWrapper } from 'pages/document/page/components/StyledComponents';
import HighlightedPolygons from 'pages/document/page/components/Polygons/highlightedPolygons';
import { isFinancialDocumentSelector } from 'domain/documents';
import PaymentInProgressBadge from './components/PaymentInProgressBadge';

const stopPropagationCallback = (cb) => (e) => {
  e.stopPropagation();
  cb();
};

export const BORDER_WIDTH = 30;
export const MAX_DOC_WIDTH = 870;

class Page extends React.Component {
  selectionArea = null;

  wrapper;

  container;

  gridButtonRef;

  startPosition = { x: 0, y: 0 };

  position = { x: 0, y: 0 };

  wheel;

  shiftPositionX = 50;

  _isMove;

  constructor(props) {
    super(props);

    this.wheel = props.scale;

    this.state = {
      showGrid: false,
      isSelection: false,
      originalFirstPageSize: {
        height: null,
        width: null,
      },
    };
  }

  static getDerivedStateFromProps(props) {
    const { isStatement } = props;

    if (isStatement) return null;
    return { showGrid: false };
  }

  componentDidMount() {
    /* global.addEventListener('keydown', this.handleKey, false);
    global.addEventListener('keyup', this.handleKey, false); */
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      polygons,
      highlightedPolygons,
      params,
      isVisibleStamp,
      stampMode,
      scale,
      width,
      isStatement,
      isFinancial,
      isPaid,
      isPaymentInProgress,
      textractEnabledForDocument,
      highlightPolygons,
    } = this.props;

    const { showGrid, isSelection } = this.state;

    if (!is(polygons, nextProps.polygons)) return true;
    if (!is(highlightedPolygons, nextProps.highlightedPolygons)) return true;
    if (!is(params, nextProps.params)) return true;
    if (isVisibleStamp !== nextProps.isVisibleStamp) return true;
    if (stampMode !== nextProps.stampMode) return true;
    if (scale !== nextProps.scale) return true;
    if (width !== nextProps.width) return true;
    if (isStatement !== nextProps.isStatement) return true;
    if (isFinancial !== nextProps.isFinancial) return true;
    if (isPaid !== nextProps.isPaid) return true;
    if (isPaymentInProgress !== nextProps.isPaymentInProgress) return true;
    if (textractEnabledForDocument !== nextProps.textractEnabledForDocument) return true;
    if (highlightPolygons !== nextProps.highlightPolygons) return true;

    if (showGrid !== nextState.showGrid) return true;
    if (isSelection !== nextState.isSelection) return true;
    return false;
  }

  componentDidUpdate(prevProps) {
    const { scale } = this.props;
    if (scale !== prevProps.scale) {
      this.shiftPositionX = scale === 1 ? 50 : this.shiftPositionX;
      this.scalePosition();
    }
  }

  componentWillUnmount() {
    global.removeEventListener('keydown', this.handleKey);
    global.removeEventListener('keyup', this.handleKey);
  }

  onRenderSuccess = (document) => (props) => {
    const { idx, onRenderSuccess } = this.props;
    const { _pageInfo } = props;
    const { view } = _pageInfo;

    if (idx === 0) {
      this.setState({
        originalFirstPageSize: {
          width: Math.abs(view[2] - view[0]),
          height: Math.abs(view[3] - view[1]),
        },
      });
    }

    onRenderSuccess(document, {
      ...props,
      pageInfo: { ..._pageInfo, view },
      pageIndex: idx,
    });
  };

  get scrollTop() {
    const { container } = this.props;
    return container ? container.scrollTop : 0;
  }

  get style() {
    const {
      params: { height },
      scale,
    } = this.props;
    return {
      width: Math.ceil(this.calculatePageWidth() * scale),
      height: Math.ceil(height),
      transform: getTransform(this.position.x),
    };
  }

  get isMove() {
    return this._isMove;
  }

  get rotateAngle() {
    const { params } = this.props;
    const { rotate, embdRotate } = params;

    return (rotate + embdRotate) % 360;
  }

  set isMove(state) {
    this._isMove = state;
    this.updateCursor(state ? '-webkit-grabbing' : null);
  }

  get scaledWidth() {
    const { scale } = this.props;
    return this.calculatePageWidth() * scale;
  }

  get ratioX() {
    const {
      polygons,
      idx,
      params: { rotate },
    } = this.props;
    const realPage = { width: parseFloat(polygons.width), height: parseFloat(polygons.height) };

    if (Number.isNaN(realPage.width) || Number.isNaN(realPage.height)) {
      throw new Error(`Incorrect width ${polygons.width} or height ${polygons.height} in page ${idx + 1}`);
    }

    const propWidth = getPropName('width', rotate);
    return this.scaledWidth / realPage[propWidth];
  }

  getStyledPolygon = (polygon) => {
    const {
      params: { height, rotate },
    } = this.props;
    return getPolygonStyleFactory(this.scaledWidth, height, rotate, this.ratioX, polygon);
  };

  getStyledHighlightedPolygon = (polygon) => {
    const {
      params: { height, rotate },
    } = this.props;
    return getPolygonStyleFactory(this.scaledWidth, height, rotate, this.ratioX, polygon);
  };

  getStyledPolygons = () => {
    const { polygons } = this.props;

    return polygons.data.reduce(
      (array, polygon) =>
        array.concat([
          {
            ...this.getStyledPolygon(polygon),
            text: polygon.text,
            id: polygon.id,
          },
        ]),
      [],
    );
  };

  getShftSizeX = () => {
    const { scale, width, container } = this.props;
    const viewPortSize = container ? container.offsetWidth : width;
    const currentPageWidth = this.calculatePageWidth() * scale;
    return Math.max(currentPageWidth - viewPortSize, 0);
  };

  setArea = (area) => {
    this.selectionArea = area;
  };

  setInitialGridPosition = (offsetY) => {
    this.mouseYPosition = offsetY;
  };

  setGridButtonRef = (gridButtonRef) => {
    this.gridButtonRef = gridButtonRef;
  };

  scalePosition = () => {
    const { isRtl } = this.props;
    const shiftAmount = Math.abs(Math.max(this.getShftSizeX(), 0)) * (this.shiftPositionX / 100);
    const shiftX = isRtl ? shiftAmount : -shiftAmount;
    this.position = { ...this.position, x: shiftX };
  };

  updateShiftPositionX = (shiftPx) => {
    this.shiftPositionX = Math.max(Math.min(100, (100 / this.getShftSizeX()) * Math.abs(shiftPx)), 0);
  };

  movePage = () => {
    if (this.container && this.isMove) {
      const { x, y } = this.position;
      const { container } = this.props;

      updateStyle(this.container, [{ name: 'transform', value: `translate3d(${x}px, ${0}px, 0)` }]);

      if (container) {
        container.scrollTo(0, y);
      }
    }
  };

  isShowButtons = () => {
    const { showGrid, isSelection } = this.state;
    const { stampMode } = this.props;
    // hide action buttons when we move approval stamp
    return !showGrid && !isSelection && stampMode !== 'move';
  };

  handleKey = (e) => {
    if (e.metaKey || e.ctrlKey) {
      this.updateCursor('default');
    } else {
      this.updateCursor(null);
      if (this.selectionArea) {
        this.selectionArea.stop();
      }
    }
  };

  handleMouseDown = (e) => {
    const { x } = this.position;
    if ((e.metaKey || e.ctrlKey) && this.selectionArea) {
      this.setState({ isSelection: true });
      const { layerX, layerY } = e.nativeEvent;
      // layerX is already relative to the target element and accounts for transforms
      this.selectionArea.start(layerX, layerY);
      return;
    }
    this.startPosition = {
      x: x ? e.clientX - x : e.clientX,
      y: this.scrollTop + e.clientY,
    };
    if (this.selectionArea) {
      this.selectionArea.areaClean();
    }
    this.isMove = true;
  };

  handleMouseMove = (event) => {
    const { isFinancial, isRtl } = this.props;

    const { nativeEvent, metaKey, ctrlKey, clientX, clientY } = event;
    const { layerX, layerY, offsetY } = nativeEvent;

    if (isFinancial) this.setInitialGridPosition(offsetY);
    if ((metaKey || ctrlKey) && this.selectionArea) {
      // layerX is already relative to the target element and accounts for transforms
      this.selectionArea.selectionAreaUpdate(layerX, layerY);
    }
    if (this.isMove) {
      const { x, y } = this.startPosition;
      const shiftSizeX = this.getShftSizeX();
      const moveX = clientX - x;
      const posX =
        shiftSizeX > 0
          ? isRtl
            ? Math.min(Math.abs(shiftSizeX), Math.max(moveX, 0)) // RTL: allow rightward movement
            : Math.max(-Math.abs(shiftSizeX), Math.min(moveX, 0)) // LTR: allow leftward movement
          : 0;
      this.updateShiftPositionX(posX);
      this.position = {
        x: posX,
        y: y - clientY,
      };
      window.requestAnimationFrame(this.movePage);
    }
  };

  handleMouseUp = () => {
    if (this.selectionArea && this.selectionArea.area) {
      this.selectionArea.stop();
      this.setState({ isSelection: false });
    }
    this.isMove = false;
  };

  handleScale = () => {
    const { scale, onScale } = this.props;
    onScale(scale + this.wheel);
  };

  throttleedScale = throttle(200, this.handleScale);

  handleWheel = (event) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      const { deltaY } = event;
      this.wheel = -(deltaY / Math.abs(deltaY)) * 0.05; // TODO: Normalize Wheel
      this.throttleedScale();
    }
  };

  calculatePageWidth = () => {
    const { width } = this.props;
    return width > MAX_DOC_WIDTH + BORDER_WIDTH ? MAX_DOC_WIDTH : width - BORDER_WIDTH;
  };

  handleGrid = () => {
    const { showGridBetaLabel } = this.props;
    this.setState(({ showGrid }) => {
      showGridBetaLabel(!showGrid);
      return { showGrid: !showGrid };
    });
  };

  updateCursor = (value) => {
    if (this.container) {
      updateStyle(this.container, [{ name: 'cursor', value }]);
    }
  };

  render() {
    const {
      params,
      idx,
      onRotate,
      scale,
      isFinancial,
      polygons,
      highlightedPolygons,
      document,
      isStatement,
      isPaid,
      isPaymentInProgress,
      isPublished,
      isVisibleStamp,
      total,
      onZoom,
      onReset,
      getIsDisabledZoomButton,
      width,
      textractEnabledForDocument,
      recognizedRotation,
      highlightPolygons,
      isRtl,
    } = this.props;

    const { showGrid, originalFirstPageSize } = this.state;
    const loop = listLoop(params.rotate / 90, angles);
    const pageContainerId = `pdfPage-${idx + 1}`;
    const deltaAngle = (360 - recognizedRotation + params.rotate) % 360;

    const onRotateLeft = stopPropagationCallback(() => onRotate(idx, loop.prev));
    const onRotateRight = stopPropagationCallback(() => onRotate(idx, loop.next));

    const isSmallActionButton = width <= 450;
    const gMaxWidth = window.innerWidth - 40;
    const getHeight = (props) => (props.scale * props.width > gMaxWidth ? props.params.height : null);

    return (
      <Box sx={{ position: 'relative', height: isFinancial ? Math.ceil(params.height) : null }}>
        <PageWrapper
          ref={(el) => {
            this.wrapper = el;
          }}
          sx={{ height: isFinancial ? Math.ceil(params.height) : getHeight(this.props) }}
        >
          <PageArea
            id={pageContainerId}
            role="presentation"
            paid={isPaid}
            paymentInProgress={isPaymentInProgress}
            style={this.style}
            onMouseDown={this.handleMouseDown}
            onMouseMove={this.handleMouseMove}
            onMouseLeave={this.handleMouseUp}
            onMouseUp={this.handleMouseUp}
            onWheel={this.handleWheel}
            ref={(el) => {
              this.container = el;
            }}
          >
            {isPaid && <PaidBadge docWidth={width} />}
            {isPaymentInProgress && !isPaid && <PaymentInProgressBadge docWidth={width} />}

            <PDFPage
              renderTextLayer={false}
              width={this.calculatePageWidth()}
              onRenderSuccess={this.onRenderSuccess(document)}
              rotate={this.rotateAngle}
              scale={scale}
              renderAnnotationLayer
              pageNumber={idx + 1}
            />
            {!isPublished && highlightPolygons && highlightedPolygons.size && (
              <HighlightedPolygons
                polygons={highlightedPolygons}
                getStyledPolygon={this.getStyledHighlightedPolygon}
                pageNumber={idx + 1}
                scale={scale}
                rotate={params.rotate}
                width={this.calculatePageWidth()}
                height={params.height}
              />
            )}

            {polygons.get('data').size && (
              <Polygons
                polygons={polygons}
                getStyledPolygons={this.getStyledPolygons}
                getStyledPolygon={this.getStyledPolygon}
                pageNumber={idx + 1}
                scale={scale}
                rotate={params.rotate}
                width={this.calculatePageWidth()}
                height={params.height}
                setArea={this.setArea}
                wrapper={this.wrapper}
              />
            )}
            {textractEnabledForDocument && (
              <TextractTableContainer
                width={this.calculatePageWidth()}
                height={params.height}
                scale={scale}
                page={idx + 1}
                wrapper={this.wrapper}
                rotation={params.rotate}
                currentAngle={this.rotateAngle}
                deltaAngle={deltaAngle}
              />
            )}
            {showGrid && (
              <Grid
                mouseYPosition={this.mouseYPosition || 0}
                handleGrid={this.handleGrid}
                width={this.scaledWidth}
                height={params.height}
                scale={scale}
                getStyledPolygons={this.getStyledPolygons}
              />
            )}
            {idx + 1 === 1 && params.status === 'complete' && isVisibleStamp && (
              <ApprovalStampManagement
                currentDimensions={new DimensionsFactory({ width: this.style.width, height: this.style.height })}
                originalDimensions={new DimensionsFactory(originalFirstPageSize)}
                rotation={params.rotate}
                isRtl={isRtl}
              />
            )}
          </PageArea>
        </PageWrapper>
        {this.isShowButtons() && (
          <DocumentPageControls
            total={total}
            currentNumber={idx + 1}
            isStatement={isStatement}
            handleGrid={this.handleGrid}
            setGridButtonRef={this.setGridButtonRef}
            onRotateLeft={onRotateLeft}
            onRotateRight={onRotateRight}
            isSmallActionButton={isSmallActionButton}
            onZoom={onZoom}
            onReset={onReset}
            getIsDisabledZoomButton={getIsDisabledZoomButton}
            isRotateDisabled={textractEnabledForDocument && isFinancial}
            stopPropagationCallback={stopPropagationCallback}
            pageWidth={this.calculatePageWidth()}
          />
        )}
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  isFinancial: isFinancialDocumentSelector(state),
});

export default connect(mapStateToProps)(Page);
